import { motion } from "framer-motion";
import { HiOutlineCursorClick, HiOutlineLibrary, HiOutlineChatAlt2, HiOutlineMailOpen } from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import PaymentChannels from "../components/PaymentChannels";
import SEO from "../components/SEO";

const PaymentInfo = () => {
    const steps = [
        {
            icon: <HiOutlineCursorClick size={24} />,
            title: "1. Selección",
            desc: "Explora el catálogo y elige el plano que se ajuste a tu proyecto."
        },
        {
            icon: <HiOutlineLibrary size={24} />,
            title: "2. Pago",
            desc: "Realiza el depósito o transferencia mediante Yape, Plin, Paypal o cuenta bancaria."
        },
        {
            icon: <HiOutlineChatAlt2 size={24} />,
            title: "3. Validación",
            desc: "Envía una captura de tu comprobante por WhatsApp o correo electrónico."
        },
        {
            icon: <HiOutlineMailOpen size={24} />,
            title: "4. Recepción",
            desc: "Enviamos tus archivos (PDF + AutoCAD) por el mismo medio que nos contactaste."
        }
    ];

    return (
        <div className="pt-32 pb-20 px-6 max-w-6xl mx-auto min-h-screen bg-white">
            <SEO
                title="Proceso de Compra de Planos Arquitectónicos"
                description="Compra planos arquitectónicos digitales en PDF y AutoCAD DWG de forma segura. Métodos de pago nacionales e internacionales disponibles."
                canonical="https://www.planixapp.com/proceso-de-compra"
                image="https://www.planixapp.com/cover.jpg"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "HowTo",
                    "name": "Cómo comprar planos arquitectónicos en Planix",
                    "description": "Proceso para adquirir planos digitales en PDF y AutoCAD.",
                    "step": [
                        {
                            "@type": "HowToStep",
                            "name": "Seleccionar plano"
                        },
                        {
                            "@type": "HowToStep",
                            "name": "Realizar pago"
                        },
                        {
                            "@type": "HowToStep",
                            "name": "Validar comprobante"
                        },
                        {
                            "@type": "HowToStep",
                            "name": "Recibir archivos"
                        }
                    ]
                }}
            />

            {/* Hero */}
            <section className="text-center mb-16">
                <h1 className="text-4xl md:text-6xl font-black text-[#1C1C1C] tracking-tighter mb-6">
                    ¿Cómo obtengo mi <span className="text-[#F26B1D]">Plano</span>?
                </h1>
                <p className="text-gray-500 max-w-xl mx-auto text-lg font-medium">
                    Sigue estos 4 sencillos pasos para adquirir tus planos técnicos de forma segura.
                </p>
            </section>

            <div className="max-w-3xl mx-auto text-center mb-16">
                <p className="text-gray-600 leading-relaxed">
                    En Planix puedes adquirir diseños de casas,
                    planos arquitectónicos modernos y archivos
                    técnicos en formato PDF y AutoCAD DWG.
                    Nuestro proceso de compra es rápido,
                    seguro y disponible para clientes de Perú,
                    Latinoamérica y otros países.
                </p>
            </div>

            {/* Timeline Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
                {steps.map((step, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="relative p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100 group hover:bg-white hover:shadow-2xl hover:shadow-[#F26B1D]/10 transition-all duration-500"
                    >
                        <div className="w-14 h-14 bg-white text-[#F26B1D] rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#F26B1D] group-hover:text-white transition-colors duration-500">
                            {step.icon}
                        </div>
                        <h3 className="text-xl font-black text-[#1C1C1C] mb-3">{step.title}</h3>
                        <p className="text-sm text-gray-500 leading-relaxed font-semibold">{step.desc}</p>

                        {/* Indicador de número */}
                        <span className="absolute top-8 right-8 text-4xl font-black text-gray-100 group-hover:text-[#F26B1D]/10 transition-colors">
                            0{index + 1}
                        </span>
                    </motion.div>
                ))}
            </div>

            {/* Sección de Métodos de Pago por Territorio */}

            <PaymentChannels />

            {/* Canales de validación */}
            <div className="grid md:grid-cols-2 gap-8">
                {/* WhatsApp Card */}
                <div className="bg-[#25D366]/5 border border-[#25D366]/20 p-8 rounded-[3rem] flex flex-col items-center text-center">
                    <FaWhatsapp className="text-[#25D366] text-5xl mb-4" />
                    <h3 className="text-2xl font-black text-[#1C1C1C] mb-2">Vía WhatsApp</h3>
                    <p className="text-gray-600 text-sm mb-6 font-medium">
                        La forma más rápida. Envía el voucher y recibe tus archivos al instante.
                    </p>
                    <a
                        href="https://wa.me/51920238477"
                        target="blank_blank"
                        className="bg-[#25D366] text-white px-8 py-3 rounded-full font-black text-xs uppercase tracking-widest hover:scale-105 transition-transform shadow-lg shadow-[#25D366]/30"
                    >
                        Enviar Comprobante
                    </a>
                </div>

                {/* Email Card */}
                <div className="bg-[#1C1C1C] p-8 rounded-[3rem] flex flex-col items-center text-center text-white">
                    <HiOutlineMailOpen className="text-[#F26B1D] text-5xl mb-4" />
                    <h3 className="text-2xl font-black mb-2">Vía Email</h3>
                    <p className="text-gray-400 text-sm mb-6 font-medium">
                        Ideal para recibir los archivos directamente en tu bandeja de entrada corporativa.
                    </p>
                    <a
                        href="mailto:axelmalqui@gmail.com?subject=Envío%20de%20Comprobante%20de%20Pago%20Planix&body=Hola%20Planix,%0A%0AAdjunto%20mi%20comprobante%20de%20pago.%0A%0AGracias!"
                        className="bg-white text-[#1C1C1C] px-8 py-3 rounded-full font-black text-xs uppercase tracking-widest hover:scale-105 transition-transform"
                    >
                        Enviar por Correo
                    </a>
                </div>
            </div>

            {/* Nota sobre formatos */}
            <p className="mt-12 text-center text-gray-400 text-[11px] font-bold uppercase tracking-[0.2em]">
                Todos los envíos incluyen archivos en formato <span className="text-[#1C1C1C]">PDF</span> y <span className="text-[#1C1C1C]">AutoCAD (DWG)</span>
            </p>
        </div>
    );
};

export default PaymentInfo;